/**
 * PicklistSelect — the ONE shared picklist control every editable panel uses for
 * a catalog-backed field (the house rule: every picklist-backed field is a
 * dropdown, not a plain text input).
 *
 * It loads the Oxbo-M resolved catalog for `categoryKey`, renders each entry's
 * `display_label`, and submits its `entry_key`. Promoted from the Cost agent's
 * `costAfe/CatalogSelect` so all panels share a single implementation.
 *
 * Graceful degradation when a catalog resolves to no entries:
 *  - if `fixedOptions` is supplied (a known small fixed enum, e.g.
 *    time_class = productive | npt | flat_waiting), it still renders a <select>
 *    over those options;
 *  - otherwise it falls back to a free-text <input> so the field is never a dead
 *    control and legacy keys can still be entered.
 *
 * A current value not present in the resolved set (a superseded / legacy key) is
 * surfaced as its own option so editing a row never silently drops it.
 */
import { useGetResolvedCatalogQuery } from "@api/mApi";

export interface FixedOption {
  value: string;
  label: string;
}

export interface PicklistSelectProps {
  categoryKey: string;
  value: string;
  onChange: (entryKey: string) => void;
  placeholder?: string;
  /** Optional visible label; when set the control is wrapped in a `.field`. */
  label?: string;
  id?: string;
  /** Fixed enum used as a select when the catalog resolves empty. */
  fixedOptions?: FixedOption[];
  /** Render a label→value option-set for the fixed enum (defaults to fixedOptions). */
  "aria-label"?: string;
}

/** A bare <select>/<input> without the `.field` wrapper — for grid cells etc. */
export function PicklistSelect({
  categoryKey,
  value,
  onChange,
  placeholder,
  label,
  id,
  fixedOptions,
  "aria-label": ariaLabel,
}: PicklistSelectProps) {
  const { data } = useGetResolvedCatalogQuery({ categoryKey });
  const entries = data?.entries ?? [];
  const resolved = entries.length > 0;

  const wrap = (control: React.ReactNode) =>
    label ? (
      <div className="field">
        <label htmlFor={id}>{label}</label>
        {control}
      </div>
    ) : (
      <>{control}</>
    );

  if (resolved) {
    // A current value not in the resolved set is kept as its own option so an
    // edit never drops a superseded key.
    const known = entries.some((e) => e.entry_key === value);
    return wrap(
      <select
        id={id}
        aria-label={ariaLabel ?? label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder ?? "—"}</option>
        {!known && value ? <option value={value}>{value}</option> : null}
        {entries.map((e) => (
          <option key={e.entry_key} value={e.entry_key}>
            {e.display_label}
          </option>
        ))}
      </select>,
    );
  }

  // Catalog empty. If the field has a known small fixed enum, still render a
  // <select> over it; else degrade to free text.
  if (fixedOptions && fixedOptions.length > 0) {
    const known = fixedOptions.some((o) => o.value === value);
    return wrap(
      <select
        id={id}
        aria-label={ariaLabel ?? label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="">{placeholder ?? "—"}</option>
        {!known && value ? <option value={value}>{value}</option> : null}
        {fixedOptions.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>,
    );
  }

  return wrap(
    <input
      id={id}
      aria-label={ariaLabel ?? label}
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />,
  );
}
